import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { nextSourceColor } from './linkSourceColors';
import { siteFallout } from './siteGraph';
import { removeScopeConfirm } from './linkFacts';
import type {
  LinkMap,
  LinkMapCandidate,
  LinkMapCandidateNode,
  LinkMapPage,
  ScopeUndo,
} from '../../types';

/**
 * Choosing what feeds a link map.
 *
 * A MODAL, not a rail. Setting scope is infrequent and deliberate — you do it
 * when you start a map and rarely after — and a permanent rail would spend the
 * workspace's width on a control used once.
 *
 * Plain checkboxes and no drag, deliberately: a second `DndProvider` mounted
 * inside the one the merged tree already owns throws, and there is nothing here
 * a drag would express anyway.
 *
 * EVERY CANDIDATE SHOWS WHAT IT WOULD BRING. A branch with no pages is a
 * legitimate thing to have and a useless thing to add, and without the number
 * the only way to find that out is to add it and see the graph not change.
 */

interface Props {
  map: LinkMap;
  onClose: () => void;
  onChanged: (map: LinkMap) => void;
  /** Removing a source here is the same destructive act as the × on a chip, so
   *  it hands back the same way to reverse it. The workspace owns the one undo
   *  stack — this modal is dismissed before you would reach for it. */
  onScopeRemoved?: (
    undo: ScopeUndo | null,
    scopeId: number,
    label: string,
  ) => void;
  /** So the confirm can say which SITES this removal takes with it — see
   *  `siteFallout`. */
  pages: LinkMapPage[];
}

function countLabel(pages: number, written: number): string {
  if (pages === 0) return 'no pages';
  if (written === 0) return `${pages} page${pages === 1 ? '' : 's'}, none written yet`;
  if (written === pages) return `${pages} page${pages === 1 ? '' : 's'}`;
  return `${pages} pages · ${written} written`;
}

export default function ScopePicker({
  map,
  onClose,
  onChanged,
  onScopeRemoved,
  pages,
}: Props) {
  const [candidates, setCandidates] = useState<LinkMapCandidate[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [nodes, setNodes] = useState<LinkMapCandidateNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      setCandidates(await api.listLinkMapCandidates(map.id));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your subjects');
    } finally {
      setLoading(false);
    }
  }, [map.id]);

  useEffect(() => {
    load();
  }, [load]);

  const expand = async (projectId: number) => {
    if (expanded === projectId) {
      setExpanded(null);
      setNodes([]);
      return;
    }
    setExpanded(projectId);
    setNodes([]);
    try {
      const full = await api.listLinkMapCandidates(map.id, projectId);
      setNodes(full.find((c) => c.id === projectId)?.nodes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that subject');
    }
  };

  const add = async (projectId: number, rootTopicId: number | null) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await api.addLinkMapScope(map.id, {
        project_id: projectId,
        root_topic_id: rootTopicId,
      });
      onChanged(updated);
      await load();
      if (expanded === projectId) await expand(projectId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that source');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (scopeId: number, label: string, edges: number) => {
    // Removing a source DELETES the links that touched it, and the count is the
    // only way to know whether that matters. The server snapshots first, but a
    // confirm that does not say the number leaves the user to find out after.
    // Shared with the × on the workspace's source chips so the two warnings
    // stay the same warning.
    if (!window.confirm(removeScopeConfirm(label, edges, siteFallout(pages, scopeId)))) {
      return;
    }
    setBusy(true);
    try {
      const updated = await api.removeLinkMapScope(map.id, scopeId);
      onChanged(updated);
      onScopeRemoved?.(updated.undo, scopeId, label);
      await load();
      if (expanded != null) await expand(expanded);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that source');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Narrow the candidate list.
   *
   * Matches a SUBJECT by its own name or by any of the sites it publishes to —
   * "which subject feeds my sunscreen site" is the question you have when you
   * are looking at the graph, and answering it by name alone would need you to
   * already know.
   *
   * An open subject keeps its branches visible when the subject itself matches,
   * so filtering never hides the branch you came to add.
   */
  const q = query.trim().toLowerCase();
  const visible = q
    ? candidates.filter((c) =>
        `${c.name} ${c.site_names.join(' ')}`.toLowerCase().includes(q),
      )
    : candidates;

  const scopeFor = (projectId: number, rootTopicId: number | null) =>
    map.scopes.find(
      (s) => s.project_id === projectId && s.root_topic_id === rootTopicId,
    );

  // The colour the NEXT source will get, shown on the button before it is
  // pressed — so the colour↔source association is learned here rather than met
  // cold in the merged tree.
  const upcoming = nextSourceColor(map.scopes);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/70" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            What goes in this map
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add a whole subject, or open one and add a single branch. Add subjects
            that publish to different websites to link between them.
          </p>

          <div className="relative mt-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a subject or site…"
              aria-label="Find a subject or site"
              className="w-full px-2 py-1.5 pr-7 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="icon-btn absolute right-1.5 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label="Clear the search"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mx-5 mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {visible.length === 0 && (
                <li className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  {candidates.length === 0
                    ? 'You have no subjects yet.'
                    : `Nothing matches “${query}”.`}
                </li>
              )}
              {visible.map((c) => {
                const scope = scopeFor(c.id, null);
                const isOpen = expanded === c.id;
                return (
                  <li key={c.id} className="py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => expand(c.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
                        title={isOpen ? 'Hide branches' : 'Show branches'}
                      >
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {c.name}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          {countLabel(c.page_count, c.written_count)}
                        </span>
                        {c.site_names.length > 0 && (
                          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                            {c.site_names.join(', ')}
                          </span>
                        )}
                      </div>

                      {scope ? (
                        <button
                          onClick={() => remove(scope.id, c.name, map.edge_count)}
                          disabled={busy}
                          className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          onClick={() => add(c.id, null)}
                          disabled={busy || c.page_count === 0}
                          title={
                            c.page_count === 0
                              ? 'This subject has no pages yet, so it would add nothing'
                              : undefined
                          }
                          className={`px-2 py-1 text-xs rounded border disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 ${upcoming.soft} ${upcoming.text} border-transparent hover:opacity-80`}
                        >
                          <span className={`w-2 h-2 rounded-full ${upcoming.dot}`} />
                          Add
                        </button>
                      )}
                    </div>

                    {isOpen && (
                      <ul className="mt-1 ml-7 border-l border-gray-100 dark:border-gray-700 pl-3">
                        {nodes.length === 0 ? (
                          <li className="py-1 text-xs text-gray-400 dark:text-gray-500">
                            No branches in this subject.
                          </li>
                        ) : (
                          nodes.map((n) => {
                            const nodeScope = scopeFor(c.id, n.id);
                            return (
                              <li
                                key={n.id}
                                className="flex items-center gap-2 py-1"
                                style={{ paddingLeft: `${n.depth * 12}px` }}
                              >
                                <span className="flex-1 min-w-0 text-xs text-gray-700 dark:text-gray-300 truncate">
                                  {n.name}
                                  <span className="ml-2 text-gray-400 dark:text-gray-500">
                                    {countLabel(n.page_count, n.written_count)}
                                  </span>
                                </span>
                                {nodeScope ? (
                                  <button
                                    onClick={() =>
                                      remove(nodeScope.id, n.name, map.edge_count)
                                    }
                                    disabled={busy}
                                    className="px-1.5 py-0.5 text-[11px] rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-50"
                                  >
                                    Remove
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => add(c.id, n.id)}
                                    disabled={busy || n.already_added || n.page_count === 0}
                                    title={
                                      n.already_added
                                        ? 'Already in this map, through the whole subject'
                                        : n.page_count === 0
                                          ? 'No pages under this branch'
                                          : undefined
                                    }
                                    className="px-1.5 py-0.5 text-[11px] rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Add
                                  </button>
                                )}
                              </li>
                            );
                          })
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
